import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRepeatAll16Regular,
  ArrowDown16Regular,
  ArrowUp16Regular,
  Building20Regular,
  CalendarAdd20Regular,
  CalendarLtr20Regular,
  ChatRegular,
  Clock20Regular,
  Location20Regular,
  TextDescription20Regular,
  CalendarToday20Regular,
  Checkmark16Regular,
  ChevronDown12Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  CopyRegular,
  Delete20Regular,
  Dismiss20Regular,
  Edit20Regular,
  Filter20Regular,
  Home20Regular,
  MoreHorizontal20Regular,
  PanelLeft20Regular,
  Video20Regular,
} from "@fluentui/react-icons";
import styles from "../Internalmessage.module.css";
import bar from "./CalendarBar.module.css";
import evp from "./Eventpopover.module.css";
import { MONTHS, PersonAvatar, SHORT_DAYS, pad2, useOutsideClose } from "../Shared/Teamsshared";
import NewEventDialog from "./NewEventDialog";
import type { CalChoice, DayBusy, NewEventForm } from "./NewEventDialog";
import {
  CalendarError,
  SPURT_PROVIDER,
  calendarMessage,
  createEvent,
  deleteEvent,
  hhmm,
  loadFeed,
  parseYmd,
  toMin,
  updateEvent,
  ymd,
} from "../spurt/calendarApi";
import type { CalDef, CalEv, EventInput, Guest } from "../spurt/calendarApi";
import { createSpurtMeeting, removeSpurtMeeting, respondToMeeting, updateSpurtMeeting } from "../spurt/meetings";

/* ------------------------------------------------------------------ *
 *  Calendar page (Teams style): mini month · work-week/week/day grid.
 *  Events live in the Cilpron calendar service (the same one your full
 *  Calendar screen uses). A "Spurt meeting" event gets a join link that
 *  only the people invited can use.
 * ------------------------------------------------------------------ */

type View = "workweek" | "week" | "day";
type Show = "online" | "other";
const VIEW_LABEL: Record<View, string> = { workweek: "Work week", week: "Week", day: "Day" };
const SHOW_LABEL: Record<Show, string> = { online: "Spurt meetings", other: "Other events" };

const ROW = 52; // px per hour
const keyOf = ymd;
const sameDay = (a: Date, b: Date): boolean => keyOf(a) === keyOf(b);
const addDays = (d: Date, n: number): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const weekStart = (d: Date, mondayFirst: boolean): Date => {
  const dow = d.getDay();
  return addDays(d, mondayFirst ? -((dow + 6) % 7) : -dow);
};
const hourLabel = (h: number): string => (h === 0 ? "" : String(h));
const AGENDA_MARK = "\n\nAgenda\n";

interface Placed {
  ev: CalEv;
  lane: number;
  lanes: number;
}
const layoutDay = (evs: CalEv[]): Placed[] => {
  const sorted = evs.slice().sort((a, b) => a.start - b.start || b.end - a.end);
  const out: Placed[] = [];
  let cluster: Placed[] = [];
  let clusterEnd = -1;
  let laneEnds: number[] = [];
  const flush = () => {
    cluster.forEach((p) => (p.lanes = laneEnds.length));
    out.push(...cluster);
    cluster = [];
    laneEnds = [];
  };
  sorted.forEach((ev) => {
    if (cluster.length && ev.start >= clusterEnd) flush();
    let lane = 0;
    while (lane < laneEnds.length && (laneEnds[lane] as number) > ev.start) lane++;
    laneEnds[lane] = ev.end;
    cluster.push({ ev, lane, lanes: 1 });
    clusterEnd = Math.max(clusterEnd, ev.end);
  });
  flush();
  return out;
};

export interface TeamsCalendarProps {
  ownerName: string;
  toast: (msg: string) => void;
  /** the page stays mounted while hidden, so it re-centres on the working day when shown */
  visible?: boolean | undefined;
  /** people the invite box can suggest (the Spurt directory) */
  directory?: Guest[] | undefined;
  /** open a meeting in Spurt; without it the join link opens in a new tab */
  onJoin?: ((ev: CalEv) => void) | undefined;
  /** open a chat with the organiser of an event */
  onChat?: ((ev: CalEv) => void) | undefined;
}

const NO_DIR: Guest[] = [];

const TeamsCalendar: React.FC<TeamsCalendarProps> = ({ ownerName, toast, visible, directory = NO_DIR, onJoin, onChat }) => {
  const [events, setEvents] = useState<CalEv[]>([]);
  const [cals, setCals] = useState<CalDef[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [note, setNote] = useState("");
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [mini, setMini] = useState<Date>(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [view, setView] = useState<View>("workweek");
  const [sideOpen, setSideOpen] = useState(true);
  const [miniOpen, setMiniOpen] = useState(true);
  const [calsOpen, setCalsOpen] = useState(true);
  const [hiddenCals, setHiddenCals] = useState<Record<string, boolean>>({});
  const [show, setShow] = useState<Record<Show, boolean>>({ online: true, other: true });
  const [pop, setPop] = useState<string | null>(null);
  const [form, setForm] = useState<NewEventForm | null>(null);
  const [formErr, setFormErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<{ ev: CalEv; day: Date; x: number; y: number } | null>(null);
  const [joinMenu, setJoinMenu] = useState(false);
  const [replying, setReplying] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const [delAsk, setDelAsk] = useState<CalEv | null>(null);
  const [now, setNow] = useState(() => new Date());
  const scrollRef = useRef<HTMLDivElement>(null);
  const seq = useRef(0);
  const closePop = useCallback(() => setPop(null), []);
  useOutsideClose(pop, closePop);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(id);
  }, []);
  const scrollTo = useCallback((hour: number) => {
    if (scrollRef.current) scrollRef.current.scrollTop = Math.max(0, hour * ROW);
  }, []);
  useEffect(() => {
    if (visible) scrollTo(7.4);
  }, [visible, scrollTo]);

  /* ---- visible days ---- */
  const days = useMemo(() => {
    if (view === "day") return [anchor];
    if (view === "week") {
      const s = weekStart(anchor, false);
      return [0, 1, 2, 3, 4, 5, 6].map((i) => addDays(s, i));
    }
    const s = weekStart(anchor, true);
    return [0, 1, 2, 3, 4].map((i) => addDays(s, i));
  }, [anchor, view]);

  /* ---- load what is on screen from the calendar service ---- */
  const first = days[0] as Date;
  const last = days[days.length - 1] as Date;
  const rangeKey = `${keyOf(first)}|${keyOf(last)}`;
  const load = useCallback(
    async (quiet = false) => {
      const mine = ++seq.current;
      if (!quiet) setStatus((s) => (s === "ready" ? s : "loading"));
      try {
        // a week either side, so the day panel in the new-event dialog can show neighbouring days
        const feed = await loadFeed(addDays(first, -7), addDays(last, 8));
        if (mine !== seq.current) return;
        setCals(feed.calendars);
        setEvents(feed.events);
        setNote("");
        setStatus("ready");
      } catch (e) {
        if (mine !== seq.current) return;
        setNote(calendarMessage(e));
        setStatus("error");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rangeKey],
  );
  useEffect(() => {
    if (visible === false) return;
    void load();
  }, [load, visible]);
  useEffect(() => {
    if (status !== "error") return;
    const t = window.setTimeout(() => void load(true), 15000);
    return () => window.clearTimeout(t);
  }, [status, load]);
  useEffect(() => {
    if (visible === false) return;
    const back = () => void load(true);
    window.addEventListener("focus", back);
    const t = window.setInterval(back, 60000);
    return () => {
      window.removeEventListener("focus", back);
      window.clearInterval(t);
    };
  }, [load, visible]);

  const calOf = (id: string): CalDef | undefined => cals.find((c) => c.id === id);

  const eventsOn = useCallback(
    (d: Date): CalEv[] => {
      const k = keyOf(d);
      return events.filter((e) => e.date === k && !hiddenCals[e.calId] && (e.online ? show.online : show.other));
    },
    [events, hiddenCals, show],
  );

  const step = view === "day" ? 1 : 7;
  const go = (n: number) => {
    const next = addDays(anchor, n * step);
    setAnchor(next);
    setMini(new Date(next.getFullYear(), next.getMonth(), 1));
  };
  const goTo = (d: Date) => {
    setAnchor(d);
    setMini(new Date(d.getFullYear(), d.getMonth(), 1));
  };
  const today = () => {
    goTo(new Date());
    scrollTo(7.4);
  };

  const rangeLabel =
    view === "day"
      ? `${first.getDate()} ${MONTHS[first.getMonth()]}, ${first.getFullYear()}`
      : first.getMonth() === last.getMonth()
        ? `${pad2(first.getDate())}–${pad2(last.getDate())} ${MONTHS[last.getMonth()]}, ${last.getFullYear()}`
        : `${pad2(first.getDate())} ${(MONTHS[first.getMonth()] ?? "").slice(0, 3)} – ${pad2(last.getDate())} ${(MONTHS[last.getMonth()] ?? "").slice(0, 3)}, ${last.getFullYear()}`;

  /* ---- mini calendar ---- */
  const miniCells = useMemo(() => {
    const s = weekStart(mini, false);
    return Array.from({ length: 42 }, (_, i) => addDays(s, i));
  }, [mini]);
  const anchorWeek = weekStart(anchor, false);
  const filterOn = !(show.online && show.other);

  /* ---- new / edit event ---- */
  const writable: CalChoice[] = useMemo(() => cals.filter((c) => c.canEdit).map((c) => ({ id: c.id, name: c.name, color: c.color })), [cals]);

  const openNew = (d: Date, startMin: number, meeting = false) => {
    if (writable.length === 0) {
      toast(status === "ready" ? "You have no calendar you can add events to." : "Your calendar is still loading.");
      return;
    }
    const s = Math.min(Math.max(0, Math.round(startMin / 15) * 15), 23 * 60);
    setFormErr("");
    setForm({
      id: null,
      title: "",
      calId: writable[0]?.id ?? "",
      date: keyOf(d),
      start: hhmm(s),
      end: hhmm(Math.min(s + 30, 24 * 60 - 1)),
      repeat: "none",
      was: "none",
      location: "",
      meeting,
      meetingUrl: "",
      people: [],
      notes: "",
      agenda: "",
    });
    setPop(null);
  };

  /** show the popover beside the event that was clicked */
  const openDetail = (ev: CalEv, day: Date, el: HTMLElement) => {
    const W = 400;
    const host = mainRef.current?.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    let x = 16;
    let y = 16;
    if (host) {
      x = r.right - host.left + 8;
      if (x + W > host.width - 8) x = r.left - host.left - W - 8;
      if (x < 8) x = Math.max(8, (host.width - W) / 2);
      y = Math.min(Math.max(8, r.top - host.top - 20), Math.max(8, host.height - 480));
    }
    setJoinMenu(false);
    setDetail({ ev, day, x, y });
  };

  const respond = async (ev: CalEv, answer: "accepted" | "tentative" | "declined") => {
    if (replying) return;
    setReplying(true);
    try {
      await respondToMeeting(ev.onlineUrl, Date.parse(ev.startAt) || Date.now(), answer);
      toast(answer === "accepted" ? "You accepted" : answer === "declined" ? "You declined" : "You replied maybe");
    } catch (err) {
      toast(err instanceof Error && err.message ? err.message : "Couldn’t send your reply");
    } finally {
      setReplying(false);
    }
  };

  const openEdit = (ev: CalEv) => {
    if (!ev.canEdit) {
      toast("You can only view this event.");
      return;
    }
    const at = ev.notes.indexOf(AGENDA_MARK);
    setFormErr("");
    setDetail(null);
    setForm({
      id: ev.id,
      title: ev.title,
      calId: ev.calId,
      date: ev.recurring ? ev.seriesDate : ev.date,
      start: hhmm(ev.start),
      end: hhmm(ev.end),
      repeat: ev.repeat,
      was: ev.repeat,
      location: ev.location,
      meeting: ev.online && ev.onlineProvider.toLowerCase() === SPURT_PROVIDER.toLowerCase(),
      meetingUrl: ev.onlineUrl,
      people: ev.guests,
      notes: at >= 0 ? ev.notes.slice(0, at) : ev.notes,
      agenda: at >= 0 ? ev.notes.slice(at + AGENDA_MARK.length) : "",
    });
  };

  const patchForm = (p: Partial<NewEventForm>) => setForm((f) => (f ? { ...f, ...p } : f));

  const busyFor = useCallback(
    (date: string): DayBusy[] => events.filter((e) => e.date === date && !(form?.id && e.id === form.id)).map((e) => ({ start: e.start, end: e.end, title: e.title })),
    [events, form?.id],
  );

  const saveForm = async () => {
    if (!form || saving) return;
    const title = form.title.trim();
    if (!title) return setFormErr("Add a title for the event.");
    if (!form.date) return setFormErr("Pick a date.");
    if (!form.calId) return setFormErr("Pick a calendar.");
    if (toMin(form.end) <= toMin(form.start)) return setFormErr("The end time must be after the start time.");
    const day = parseYmd(form.date);
    const iso = (m: number) => new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, m).toISOString();
    const description = form.notes.trim() + (form.agenda.trim() ? (form.notes.trim() ? AGENDA_MARK : "Agenda\n") + form.agenda.trim() : "");
    const recurrence = form.repeat !== "custom" && (form.id === null ? form.repeat !== "none" : form.repeat !== form.was) ? form.repeat : undefined;

    setSaving(true);
    setFormErr("");
    try {
      const meetingArgs = { title, startAt: iso(toMin(form.start)), endAt: iso(toMin(form.end)), guests: form.people, agenda: form.agenda.trim(), notes: form.notes.trim(), repeat: form.repeat };
      const input: EventInput = {
        calendarId: form.calId,
        title,
        description,
        location: form.location.trim(),
        date: form.date,
        start: form.start,
        end: form.end,
        guests: form.people,
        onlineProvider: form.meeting ? SPURT_PROVIDER : "",
        ...(form.meeting && form.meetingUrl ? { onlineUrl: form.meetingUrl } : {}),
        ...(recurrence ? { recurrence } : {}),
      };
      if (form.id === null && form.meeting) {
        // a new meeting: the server adds the calendar event itself when its calendar link is on, otherwise this adds it
        await createSpurtMeeting(meetingArgs, { calendarId: form.calId, location: form.location.trim(), description });
      } else if (form.id === null) {
        await createEvent(input);
      } else {
        if (form.meeting && form.meetingUrl) await updateSpurtMeeting(form.meetingUrl, meetingArgs);
        await updateEvent(form.id, input);
      }
      // turned the switch off on an event that had a meeting
      if (!form.meeting && form.meetingUrl) await removeSpurtMeeting(form.meetingUrl).catch(() => undefined);
      const created = form.id === null;
      const when = form.date;
      setForm(null);
      toast(created ? (form.meeting ? "Meeting scheduled" : "Event created") : "Event updated");
      const d = parseYmd(when);
      if (view === "workweek" && (d.getDay() === 0 || d.getDay() === 6)) setView("week");
      goTo(d);
      void load(true);
    } catch (err) {
      setFormErr(err instanceof CalendarError ? err.message : err instanceof Error && err.message ? err.message : calendarMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const removeEvent = async (ev: CalEv, scope: "this" | "all") => {
    setDelAsk(null);
    setDetail(null);
    const prev = events;
    try {
      setEvents((p) => (scope === "this" ? p.filter((x) => !(x.id === ev.id && x.date === ev.date)) : p.filter((x) => x.id !== ev.id)));
      await deleteEvent(ev, scope);
      if (scope === "all" && ev.onlineUrl && ev.onlineProvider.toLowerCase() === SPURT_PROVIDER.toLowerCase()) {
        await removeSpurtMeeting(ev.onlineUrl).catch(() => undefined); // cancel the meeting behind the event
      }
      toast(scope === "this" ? "Event deleted" : ev.recurring ? "Series deleted" : "Event deleted");
    } catch (err) {
      setEvents(prev);
      toast(calendarMessage(err));
    }
  };
  const askDelete = (ev: CalEv) => {
    if (ev.recurring) setDelAsk(ev);
    else void removeEvent(ev, "all");
  };

  const join = (ev: CalEv) => {
    if (onJoin) onJoin(ev);
    else if (ev.onlineUrl) window.open(ev.onlineUrl, "_blank", "noopener");
    else toast("This event has no meeting link yet.");
  };
  const copyLink = async (ev: CalEv) => {
    try {
      await navigator.clipboard.writeText(ev.onlineUrl);
      toast("Meeting link copied");
    } catch {
      toast("Couldn’t copy the link");
    }
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key !== "Escape") return;
    if (form || detail || pop || delAsk) {
      e.stopPropagation();
      if (delAsk) setDelAsk(null);
      else if (form) !saving && setForm(null);
      else if (detail) setDetail(null);
      else setPop(null);
    }
  };

  const nowMin = now.getHours() * 60 + now.getMinutes();
  const defaultDay = days.some((d) => sameDay(d, anchor)) ? anchor : first;
  const meetNow = () => {
    openNew(new Date(), nowMin, true);
  };

  return (
    <div className={styles.calPage} onKeyDown={onKey} tabIndex={-1}>
      {/* ───────── side panel ───────── */}
      {sideOpen && (
        <aside className={styles.calSide} aria-label="Calendar navigation">
          <h2 className={styles.calSideTitle}>Calendar</h2>

          <div className={styles.miniHead}>
            <button type="button" className={styles.miniToggle} aria-expanded={miniOpen} onClick={() => setMiniOpen(!miniOpen)}>
              <ChevronDown12Regular style={{ transform: miniOpen ? "none" : "rotate(-90deg)" }} />
              {MONTHS[mini.getMonth()]} {mini.getFullYear()}
            </button>
            <span className={styles.grow} />
            <button type="button" className={styles.miniNav} aria-label="Previous month" onClick={() => setMini(new Date(mini.getFullYear(), mini.getMonth() - 1, 1))}>
              <ArrowUp16Regular />
            </button>
            <button type="button" className={styles.miniNav} aria-label="Next month" onClick={() => setMini(new Date(mini.getFullYear(), mini.getMonth() + 1, 1))}>
              <ArrowDown16Regular />
            </button>
          </div>

          {miniOpen && (
            <div className={styles.miniGrid} role="grid" aria-label={`${MONTHS[mini.getMonth()]} ${mini.getFullYear()}`}>
              {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                <span key={i} className={styles.miniDow}>
                  {d}
                </span>
              ))}
              {miniCells.map((d) => {
                const inWeek = view !== "day" && sameDay(weekStart(d, false), anchorWeek);
                const isToday = sameDay(d, now);
                const dow = d.getDay();
                const edgeL = inWeek && dow === 0;
                const edgeR = inWeek && dow === 6;
                return (
                  <button
                    key={keyOf(d)}
                    type="button"
                    className={`${styles.miniDay} ${inWeek ? styles.miniInWeek : ""} ${edgeL ? styles.miniL : ""} ${edgeR ? styles.miniR : ""} ${d.getMonth() !== mini.getMonth() ? styles.miniOut : ""}`}
                    aria-label={d.toDateString()}
                    aria-current={isToday ? "date" : undefined}
                    onClick={() => goTo(d)}
                  >
                    <span className={isToday ? styles.miniToday : ""}>{d.getDate()}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className={styles.miniRule} />

          <button type="button" className={styles.sideSection} aria-expanded={calsOpen} onClick={() => setCalsOpen(!calsOpen)}>
            <ChevronDown12Regular style={{ transform: calsOpen ? "none" : "rotate(-90deg)" }} />
            My calendars
          </button>
          {calsOpen &&
            cals.map((c) => (
              <label key={c.id} className={styles.calCheck}>
                <input type="checkbox" checked={!hiddenCals[c.id]} onChange={(e) => setHiddenCals({ ...hiddenCals, [c.id]: !e.target.checked })} />
                <span className={`${styles.checkBox} ${!hiddenCals[c.id] ? styles.checkOn : ""}`} aria-hidden="true" style={!hiddenCals[c.id] ? { background: c.color, borderColor: c.color } : undefined}>
                  {!hiddenCals[c.id] && <Checkmark16Regular />}
                </span>
                {c.name}
              </label>
            ))}
          <button type="button" className={styles.linkText} onClick={() => { setHiddenCals({}); setShow({ online: true, other: true }); }}>
            Show all
          </button>
        </aside>
      )}

      {/* ───────── main ───────── */}
      <section className={styles.calMain} aria-label="Calendar" ref={mainRef}>
        <div className={styles.calBar}>
          <button type="button" className={styles.iconBtn} aria-label={sideOpen ? "Hide calendar panel" : "Show calendar panel"} title="Toggle panel" onClick={() => setSideOpen(!sideOpen)}>
            <PanelLeft20Regular />
          </button>
          <span className={styles.barRule} />
          <button type="button" className={styles.barBtn} onClick={today}>
            <CalendarToday20Regular />
            Today
          </button>
          <button type="button" className={styles.iconBtn} aria-label="Previous" onClick={() => go(-1)}>
            <ChevronLeft20Regular />
          </button>
          <button type="button" className={styles.iconBtn} aria-label="Next" onClick={() => go(1)}>
            <ChevronRight20Regular />
          </button>
          <div className={styles.anchor} data-pop="range">
            <button type="button" className={styles.rangeBtn} aria-haspopup="dialog" aria-expanded={pop === "range"} onClick={() => setPop(pop === "range" ? null : "range")}>
              {rangeLabel}
              <ChevronDown12Regular />
            </button>
            {pop === "range" && (
              <div className={`${styles.pop} ${styles.popPad}`} role="dialog" aria-label="Go to date">
                <label className={styles.fieldLbl} htmlFor="cal-goto">
                  Go to date
                </label>
                <input id="cal-goto" type="date" className={styles.field} value={keyOf(anchor)} onChange={(e) => e.target.value && goTo(parseYmd(e.target.value))} />
              </div>
            )}
          </div>

          <span className={styles.grow} />

          <div className={styles.anchor} data-pop="view">
            <button type="button" className={styles.barBtn} aria-haspopup="menu" aria-expanded={pop === "view"} onClick={() => setPop(pop === "view" ? null : "view")}>
              <CalendarLtr20Regular />
              {VIEW_LABEL[view]}
              <ChevronDown12Regular />
            </button>
            {pop === "view" && (
              <div className={`${styles.pop} ${styles.popRight}`} role="menu">
                {(Object.keys(VIEW_LABEL) as View[]).map((v) => (
                  <button key={v} type="button" role="menuitemradio" aria-checked={view === v} className={styles.popItem} onClick={() => { setView(v); setPop(null); }}>
                    <span className={styles.tick}>{view === v && <Checkmark16Regular />}</span>
                    {VIEW_LABEL[v]}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className={styles.anchor} data-pop="filter">
            <button type="button" className={`${styles.barBtn} ${styles.barBtnOutline}`} aria-haspopup="menu" aria-expanded={pop === "filter"} onClick={() => setPop(pop === "filter" ? null : "filter")}>
              <Filter20Regular />
              {filterOn ? "Filter applied" : "Filter"}
              <ChevronDown12Regular />
            </button>
            {pop === "filter" && (
              <div className={`${styles.pop} ${styles.popRight}`} role="menu">
                {(Object.keys(SHOW_LABEL) as Show[]).map((k) => (
                  <button key={k} type="button" role="menuitemcheckbox" aria-checked={show[k]} className={styles.popItem} onClick={() => setShow({ ...show, [k]: !show[k] })}>
                    <span className={styles.tick}>{show[k] && <Checkmark16Regular />}</span>
                    {SHOW_LABEL[k]}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className={styles.anchor} data-pop="more">
            <button type="button" className={styles.iconBtn} aria-label="More options" aria-haspopup="menu" aria-expanded={pop === "more"} onClick={() => setPop(pop === "more" ? null : "more")}>
              <MoreHorizontal20Regular />
            </button>
            {pop === "more" && (
              <div className={`${styles.pop} ${styles.popRight}`} role="menu">
                <button type="button" role="menuitem" className={styles.popItem} onClick={() => { today(); setPop(null); }}>
                  Go to today
                </button>
                <button type="button" role="menuitem" className={styles.popItem} onClick={() => { scrollTo(0); setPop(null); }}>
                  Scroll to midnight
                </button>
                <button type="button" role="menuitem" className={styles.popItem} onClick={() => { void load(); setPop(null); }}>
                  Refresh
                </button>
              </div>
            )}
          </div>
          <span className={styles.barRule} />
          <button type="button" className={`${styles.barBtn} ${bar.nowrap}`} onClick={meetNow} title="Schedule a meeting" aria-label="Schedule a meeting">
            <Video20Regular />
            <span className={bar.label}>Schedule a meeting</span>
          </button>
          <div className={`${styles.split} ${bar.split}`}>
            <button type="button" className={`${styles.splitMain} ${bar.splitMain}`} onClick={() => openNew(defaultDay, nowMin)}>
              <CalendarAdd20Regular />
              New
            </button>
            <div className={styles.anchor} data-pop="new">
              <button type="button" className={`${styles.splitArrow} ${bar.splitArrow}`} aria-label="New options" aria-haspopup="menu" aria-expanded={pop === "new"} onClick={() => setPop(pop === "new" ? null : "new")}>
                <ChevronDown12Regular />
              </button>
              {pop === "new" && (
                <div className={`${styles.pop} ${styles.popRight}`} role="menu">
                  <button type="button" role="menuitem" className={styles.popItem} onClick={() => openNew(defaultDay, nowMin, false)}>
                    New event
                  </button>
                  <button type="button" role="menuitem" className={styles.popItem} onClick={() => openNew(defaultDay, nowMin, true)}>
                    New Spurt meeting
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {status !== "ready" && (
          <div role="status" style={{ padding: "6px 16px", fontSize: 13, color: status === "error" ? "#b10e1c" : "#616161" }}>
            {status === "loading" ? "Loading your calendar…" : note}
            {status === "error" && (
              <button type="button" style={{ marginLeft: 8, border: 0, background: "none", color: "#0f548c", cursor: "pointer" }} onClick={() => void load()}>
                Try again
              </button>
            )}
          </div>
        )}

        {/* grid */}
        <div className={styles.dayHead} style={{ gridTemplateColumns: `52px repeat(${days.length}, minmax(0, 1fr))` }}>
          <span />
          {days.map((d, i) => {
            const isToday = sameDay(d, now);
            return (
              <div key={keyOf(d)} className={`${styles.dayCell} ${isToday ? styles.dayToday : ""}`}>
                <span className={styles.dayNum}>{pad2(d.getDate())}</span>
                <span className={styles.dayName}>{SHORT_DAYS[d.getDay()]}</span>
                <span className={styles.dayIcon} aria-hidden="true">
                  {i % 2 === 0 ? <Building20Regular /> : <Home20Regular />}
                </span>
              </div>
            );
          })}
        </div>

        <div className={styles.grid} ref={scrollRef}>
          <div className={styles.gridInner} style={{ gridTemplateColumns: `52px repeat(${days.length}, minmax(0, 1fr))`, height: ROW * 24 }}>
            <div className={styles.hours}>
              {Array.from({ length: 24 }, (_, h) => (
                <span key={h} style={{ top: h * ROW }}>
                  {hourLabel(h)}
                </span>
              ))}
            </div>
            {days.map((d) => {
              const placed = layoutDay(eventsOn(d));
              const isToday = sameDay(d, now);
              return (
                <div
                  key={keyOf(d)}
                  className={styles.dayCol}
                  onDoubleClick={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    openNew(d, ((e.clientY - r.top) / ROW) * 60);
                  }}
                  title="Double-click to add an event"
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <span key={h} className={styles.hourLine} style={{ top: h * ROW }} />
                  ))}
                  {placed.map(({ ev, lane, lanes }) => {
                    const h = Math.max(((ev.end - ev.start) / 60) * ROW - 2, 22);
                    const color = calOf(ev.calId)?.color;
                    return (
                      <button
                        key={ev.id + "|" + ev.date}
                        type="button"
                        className={`${styles.event} ${ev.online ? "" : styles.eventFocus}`}
                        style={{ top: (ev.start / 60) * ROW + 1, height: h, left: `calc(${(lane / lanes) * 100}% + 3px)`, width: `calc(${100 / lanes}% - 6px)`, ...(color && ev.online ? { borderLeft: `3px solid ${color}` } : {}) }}
                        onClick={(e) => openDetail(ev, d, e.currentTarget)}
                        onDoubleClick={(e) => e.stopPropagation()}
                        aria-label={`${ev.title}, ${hhmm(ev.start)} to ${hhmm(ev.end)}`}
                      >
                        <span className={styles.eventTitle}>{ev.title}</span>
                        {h > 34 && <span className={styles.eventOrg}>{ev.organizer === "You" ? ownerName : ev.organizer}</span>}
                        {ev.online && <Video20Regular style={{ position: "absolute", right: 4, top: 4, width: 14, height: 14 }} />}
                        {ev.recurring && <ArrowRepeatAll16Regular className={styles.eventRepeat} />}
                      </button>
                    );
                  })}
                  {isToday && <span className={styles.nowLine} style={{ top: (nowMin / 60) * ROW }} aria-hidden="true" />}
                </div>
              );
            })}
          </div>
        </div>

        <NewEventDialog
          form={form}
          calendars={writable}
          error={formErr}
          saving={saving}
          busyFor={busyFor}
          directory={directory}
          onChange={patchForm}
          onSave={() => void saveForm()}
          onCancel={() => !saving && setForm(null)}
          onToast={toast}
        />

        {/* event popover */}
        {detail && (() => {
          const ev = detail.ev;
          const spurt = ev.online && ev.onlineProvider.toLowerCase() === SPURT_PROVIDER.toLowerCase();
          const others = ev.guests.length;
          const when = `${detail.day.toLocaleDateString("en-GB", { weekday: "short" })} ${pad2(detail.day.getDate())}/${pad2(detail.day.getMonth() + 1)}/${detail.day.getFullYear()} ${hhmm(ev.start)} - ${hhmm(ev.end)}`;
          const org = ev.organizer === "You" ? ownerName : ev.organizer;
          return (
            <>
              <div className={evp.veil} onMouseDown={() => setDetail(null)} />
              <div className={evp.pop} style={{ left: detail.x, top: detail.y }} role="dialog" aria-label="Event details">
                <div className={evp.head}>
                  <h3 className={evp.title}>{ev.title}</h3>
                  <button type="button" className={evp.x} aria-label="Close" onClick={() => setDetail(null)}>
                    <Dismiss20Regular />
                  </button>
                </div>
                {(ev.online || (onChat && !ev.mine)) && (
                  <div className={evp.btns}>
                    {ev.online && (
                      <div className={evp.split}>
                        <button type="button" className={evp.splitMain} onClick={() => { setDetail(null); join(ev); }}>
                          <Video20Regular />
                          Join
                        </button>
                        <button type="button" className={evp.splitArrow} aria-label="More join options" aria-haspopup="menu" aria-expanded={joinMenu} onClick={() => setJoinMenu(!joinMenu)}>
                          <ChevronDown12Regular />
                        </button>
                        {joinMenu && (
                          <div className={evp.menu} role="menu">
                            {ev.onlineUrl && (
                              <button type="button" role="menuitem" onClick={() => { setJoinMenu(false); void copyLink(ev); }}>
                                <CopyRegular />
                                Copy meeting link
                              </button>
                            )}
                            <button type="button" role="menuitem" onClick={() => { setDetail(null); join(ev); }}>
                              <Video20Regular />
                              Join now
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {onChat && !ev.mine && (
                      <button type="button" className={evp.btn} onClick={() => { setDetail(null); onChat(ev); }}>
                        <ChatRegular />
                        Chat
                      </button>
                    )}
                  </div>
                )}
                <div className={evp.row}>
                  <Clock20Regular />
                  <span>
                    {when}
                    {ev.recurring && (
                      <span className={evp.series}>
                        Series <ArrowRepeatAll16Regular />
                      </span>
                    )}
                  </span>
                </div>
                <div className={evp.row}>
                  <Location20Regular />
                  <span>{ev.location || "No location added"}</span>
                </div>
                <div className={evp.row}>
                  <PersonAvatar name={org} image={null} size={32} />
                  <span>
                    {ev.mine ? "You organised this." : `${org} invited you.`}
                    <span className={evp.small}>{others === 0 ? "No other guests" : `${others} ${others === 1 ? "guest" : "guests"}: ${ev.guests.slice(0, 3).map((g) => g.name || g.email).join(", ")}${others > 3 ? "…" : ""}`}</span>
                  </span>
                </div>
                {ev.notes && (
                  <div className={evp.row}>
                    <TextDescription20Regular />
                    <span className={evp.notes}>{ev.notes}</span>
                  </div>
                )}
                {spurt && !ev.mine && (
                  <div className={evp.reply} role="group" aria-label="Your reply">
                    <button type="button" className={evp.btn} disabled={replying} onClick={() => void respond(ev, "accepted")}>
                      <Checkmark16Regular style={{ color: "#107c10" }} />
                      Accept
                    </button>
                    <button type="button" className={evp.btn} disabled={replying} onClick={() => void respond(ev, "tentative")}>
                      Maybe
                    </button>
                    <button type="button" className={evp.btn} disabled={replying} onClick={() => void respond(ev, "declined")}>
                      <Dismiss20Regular style={{ color: "#c50f1f", width: 16, height: 16 }} />
                      Decline
                    </button>
                  </div>
                )}
                {ev.canEdit && (
                  <div className={evp.foot}>
                    <button type="button" className={evp.btn} style={{ fontSize: 15, height: 34 }} onClick={() => askDelete(ev)}>
                      <Delete20Regular />
                      {ev.recurring ? "Delete series" : "Delete"}
                    </button>
                    <button type="button" className={evp.btn} style={{ fontSize: 15, height: 34 }} onClick={() => openEdit(ev)}>
                      <Edit20Regular />
                      Edit
                    </button>
                  </div>
                )}
              </div>
            </>
          );
        })()}

        {delAsk && (
          <div className={styles.dlgWrap} onMouseDown={(e) => e.target === e.currentTarget && setDelAsk(null)}>
            <div className={styles.dlg} role="dialog" aria-modal="true" aria-label="Delete repeating event">
              <div className={styles.dlgHead}>
                <h3>Delete repeating event</h3>
              </div>
              <div className={styles.detailRow}>“{delAsk.title}” repeats. What do you want to delete?</div>
              <div className={styles.dlgFoot}>
                <button type="button" className={styles.outlineBtn} onClick={() => setDelAsk(null)}>
                  Cancel
                </button>
                <button type="button" className={styles.outlineBtn} onClick={() => void removeEvent(delAsk, "this")}>
                  Only this event
                </button>
                <button type="button" className={styles.primaryBtn} onClick={() => void removeEvent(delAsk, "all")}>
                  All events
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default TeamsCalendar;